import React, {CSSProperties, useEffect} from "react";
import ReactDOM from "react-dom";

type TooltipProps = {
    title: string;
    delay?: number;
    children: React.ReactElement;
}


export const Tooltip = React.forwardRef((props: TooltipProps, ref: React.ForwardedRef<HTMLElement>) => {

    // XXX: ブラウザ前提
    const enter_timer = React.useRef<number>(0);

    const delay = props.delay === undefined || props.delay < 0 ? 500 : props.delay;

    const [isOpen, setIsOpen] = React.useState<boolean>(false);
    const [childElement, setChildElement] = React.useState<Element | null>(null);

    const handleOpen = () => {
        setIsOpen(true);
    };
    const handleClose = () => {
        setIsOpen(false);
    };

    const handleMouseOver = (e: React.MouseEvent<HTMLElement>) => {
        if (childElement === null) {
            setChildElement(e.target as Element);
        }

        if (props.children.props.onMouseOver) {
            props.children.props.onMouseOver(e);
        }

        clearTimeout(enter_timer.current);
        enter_timer.current = window.setTimeout(() => {
            handleOpen();
        }, delay);
    };
    const handleMouseLeave = (e: React.MouseEvent) => {
        if (props.children.props.onMouseLeave) {
            props.children.props.onMouseLeave(e);
        }

        clearTimeout(enter_timer.current);
        handleClose();
    };

    const children_props = {...props.children.props};
    children_props.onMouseOver = handleMouseOver;
    children_props.onMouseLeave = handleMouseLeave;
    children_props.ref = ref;

    return (
        <>
            {React.cloneElement(props.children, children_props)}
            {isOpen && props.title !== '' && (
                <TooltipPopper anchorElement={childElement}>{props.title}</TooltipPopper>
            )}
        </>
    );
});

type TooltipPopperProps = {
    anchorElement: Element | null;
    children: React.ReactNode;
};

const TooltipPopper = (props: TooltipPopperProps) => {

    const ref = React.useRef<HTMLDivElement>(null);
    const [top, setTop] = React.useState<number>(0);
    const [left, setLeft] = React.useState<number>(0);

    //console.log("TooltipPopper.anchorElement", props.anchorElement, ref.current);
    useEffect(() => {
        if (props.anchorElement && ref.current) {
            const rect = props.anchorElement.getBoundingClientRect();
            setTop(window.scrollY + rect.bottom + 5);
            setLeft(window.scrollX + rect.x + (rect.width / 2) - (ref.current.clientWidth / 2));
        }
    }, [props.anchorElement]);

    const adjust_x = left < 0 ? left : 0;

    const style: React.CSSProperties = {
        display: 'block',
        visibility: (top === 0 && left === 0) ? 'hidden' : 'visible',
        position: 'absolute',
        top,
        left: 0,
        transform: `translateX(${left - adjust_x}px)`, /* left で指定すると、ウィンドウ右端を超える位置の場合にテキスト幅が適切に確保されない */
        padding: '5px 10px',
        borderRadius: '5px',
        backgroundColor: 'rgb(0, 104, 183, 0.8)',
        zIndex: 5000,
        fontSize: 12,
        fontWeight: 'bold',
        color: '#FFF',
    };
    const triangle_style = {
        display: 'block',
        position: 'absolute',
        top: -10,
        left: adjust_x ? `calc(50% - 5px + ${adjust_x}px)` : 'calc(50% - 5px)',
        border: '5px solid transparent',
        borderBottomColor: 'rgb(0, 104, 183, 0.8)'
    } as CSSProperties


    const component = (
        <div role="tooltip" ref={ref} style={style}>
            {props.children}
            <span style={triangle_style}/>
        </div>
    );

    return ReactDOM.createPortal(component, document.body);
}
